import React, { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as HappyBuddy } from '../../../../../../assets/icons/buddy-happy-open-eyes.svg';
import { DELETE_V3, NORTH_EAST_ARROW } from '../../../../../constants/images';
import {
  CLICK_ACTION,
  DELETE_CHALLENGE,
  DELETE_CHALLENGE_CANCEL,
  DELETE_CHALLENGE_CONFIRM,
  DELETE_CHALLENGE_ERROR
} from '../../../../../constants/tagManager';
import { DELETE_CHALLENGE_ERROR_TOAST } from '../../../../../constants/toast';
import { IChallengeDetail } from '../../../../../interfaces/challenges';
import Challenges from '../../../../../services/api/challenges/challenges';
import TagManagerService from '../../../../../services/gtm/tagManagerService';
import { TRANSLATIONS_CHALLENGES_MAIN } from '../../../../../translations/challenges';
import { GenericFunction } from '../../../../../types/genericTypes';
import BdsTooltip from '../../../BdsTooltip/BdsTooltip';
import BdsButton from '../../../Buttons/BdsButton/BdsButton';
import BdsIconButton from '../../../Buttons/BdsIconButton/BdsIconButton';
import BdsModal from '../../../Modals/BdsModal/BdsModal';
import Toast from '../../../Toast/Toast';

import './ChallengesTableActions.scss';

interface IChallengeTableActionsProps {
  challenge: IChallengeDetail;
  isActive?: boolean;
  action: GenericFunction;
  onChange: GenericFunction;
}

const ChallengesTableActions = ({ challenge, isActive, action, onChange }: IChallengeTableActionsProps) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [openToast, setOpenToast] = useState<boolean>(false);

  const { t } = useTranslation();

  const disableDelete = useMemo(
    () => !isActive || !!challenge.participants || challenge.author === 'Betterfly',
    [isActive, challenge]
  );

  const handleCellClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  const openDeleteModal = () => {
    TagManagerService.pushEvent(CLICK_ACTION, DELETE_CHALLENGE);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    TagManagerService.pushEvent(CLICK_ACTION, DELETE_CHALLENGE_CANCEL);
    setIsDeleteModalOpen(false);
  };

  const onDelete = async () => {
    setLoading(true);
    try {
      TagManagerService.pushEvent(CLICK_ACTION, DELETE_CHALLENGE_CONFIRM);
      await Challenges.updateChallenges(challenge.id);
      onChange(challenge.id);
    } catch {
      setOpenToast(true);
      TagManagerService.pushEvent(CLICK_ACTION, DELETE_CHALLENGE_ERROR);
    } finally {
      setLoading(false);
      setIsDeleteModalOpen(false);
    }
  };

  const handleCloseToast = () => {
    setOpenToast(false);
  };

  const renderDeleteButton = () => {
    if (disableDelete) {
      const tooltipMessage =
        challenge.author === 'Betterfly'
          ? TRANSLATIONS_CHALLENGES_MAIN.deleteChallenge.betterfly
          : TRANSLATIONS_CHALLENGES_MAIN.deleteChallenge.noCollaborators;
      return (
        <BdsTooltip message={t(tooltipMessage)} position="left">
          <BdsIconButton
            icon={DELETE_V3}
            action={openDeleteModal}
            variant="transparent"
            size="sm"
            disabled={disableDelete}
            label="delete"
          />
        </BdsTooltip>
      );
    }
    return (
      <BdsIconButton
        icon={DELETE_V3}
        action={openDeleteModal}
        variant="transparent"
        size="sm"
        disabled={!isActive || challenge.status === 'finished'}
        label="delete"
      />
    );
  };

  return (
    <div className="challenges-table-actions">
      <div className="challenges-table-actions-buttons" onClick={handleCellClick} role="presentation">
        <div className="hide-desktop">
          <BdsButton variant="primary" size="s" iconRight={NORTH_EAST_ARROW} action={action}>
            {t(TRANSLATIONS_CHALLENGES_MAIN.table.seeRanking)}
          </BdsButton>
        </div>
        {renderDeleteButton()}
        <BdsModal
          open={isDeleteModalOpen}
          title={t(TRANSLATIONS_CHALLENGES_MAIN.deleteChallenge.title)}
          description={t(TRANSLATIONS_CHALLENGES_MAIN.deleteChallenge.description)}
          icon={<HappyBuddy />}
          loading={loading}
          primaryAction={{
            label: t(TRANSLATIONS_CHALLENGES_MAIN.deleteChallenge.primaryButton),
            onClick: onDelete
          }}
          secondaryAction={{
            label: t(TRANSLATIONS_CHALLENGES_MAIN.deleteChallenge.secondaryButton),
            onClick: handleCloseDeleteModal
          }}
        />
        <Toast
          title={t(DELETE_CHALLENGE_ERROR_TOAST.title)}
          description={t(DELETE_CHALLENGE_ERROR_TOAST.description)}
          icon={DELETE_CHALLENGE_ERROR_TOAST.icon}
          open={openToast}
          button={DELETE_CHALLENGE_ERROR_TOAST.button}
          buttonAction={handleCloseToast}
          handleClose={handleCloseToast}
        />
      </div>
    </div>
  );
};

export default ChallengesTableActions;
